import React from "react";
import { Youtube, Heart, Pencil, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatNumber } from "@/lib/utils";
import { STATUS_OPTIONS } from "../constants";
import { getAvgViews, getSocialProfileUrl } from "../lib/influencerCalculations";
import type { CampaignInfluencer } from "../types";

interface AssignedInfluencersTableProps {
  influencers: CampaignInfluencer[];
  onViewProfile: (influencerId: string) => void;
  onStatusChange: (influencerId: string, newStatus: string) => void;
  isUpdatingStatus: boolean;
  onEdit: (influencer: CampaignInfluencer) => void;
  onDelete: (influencer: CampaignInfluencer) => void;
}

export const AssignedInfluencersTable: React.FC<AssignedInfluencersTableProps> = ({
  influencers,
  onViewProfile,
  onStatusChange,
  isUpdatingStatus,
  onEdit,
  onDelete,
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[28%]">Influencer Name</TableHead>
          <TableHead className="text-center w-[8%]">Platform</TableHead>
          <TableHead className="text-center w-[10%]">Followers</TableHead>
          <TableHead className="text-center w-[10%]">Average Views</TableHead>
          <TableHead className="text-center w-[10%]">Engagement Rate</TableHead>
          <TableHead className="w-[10%]">Deliverables</TableHead>
          <TableHead className="w-[8%]">Fees</TableHead>
          <TableHead className="w-[12%]">Status</TableHead>
          <TableHead className="w-[10%]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {influencers.map((influencer) => (
          <TableRow key={influencer.id}>
            <TableCell>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                  {influencer.profilePicUrl ? (
                    <img
                      src={influencer.profilePicUrl}
                      alt={influencer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-xs font-semibold">
                      {influencer.name?.substring(0, 2).toUpperCase() || "??"}
                    </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <span
                    className="font-medium hover:text-[#A655F8] hover:underline cursor-pointer transition-colors"
                    onClick={() => onViewProfile(influencer.id)}
                  >
                    {influencer.name}
                  </span>
                  {influencer.handle && (
                    <a
                      href={getSocialProfileUrl(influencer.platform, influencer.handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      @{influencer.handle.replace(/^@/, "")}
                    </a>
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center justify-center space-x-2">
                {influencer.platform?.toLowerCase() === "instagram" ? (
                  <img
                    src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                    alt="Instagram"
                    className="w-5 h-5"
                  />
                ) : influencer.platform?.toLowerCase() === "youtube" ? (
                  <Youtube className="w-5 h-5 text-red-500" />
                ) : (
                  <Badge variant="outline">{influencer.platform}</Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="text-center font-medium">
              {influencer.followersCount
                ? formatNumber(influencer.followersCount)
                : "-"}
            </TableCell>
            <TableCell className="text-center font-medium text-slate-700">
              {getAvgViews(influencer)}
            </TableCell>
            <TableCell className="text-center font-medium">
              <div className="flex items-center justify-center space-x-1">
                <Heart className="w-4 h-4 text-gray-400" />
                <span>
                  {influencer.engagementRate != null
                    ? `${influencer.engagementRate}%`
                    : "-"}
                </span>
              </div>
            </TableCell>

            <TableCell>
              <span className="text-sm text-gray-600">
                {influencer.deliverables || "Not specified"}
              </span>
            </TableCell>
            <TableCell>
              <span className="text-sm">
                {influencer.totalPayment || "Not set"}
              </span>
            </TableCell>
            <TableCell>
              <Select
                value={influencer.status}
                onValueChange={(value) =>
                  onStatusChange(influencer.id, value)
                }
                disabled={isUpdatingStatus}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell className="text-right w-[10%]">
              <div className="flex items-center justify-end space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-500 hover:text-[#A655F8] hover:bg-[#A655F8]/[0.06]"
                  onClick={() => onEdit(influencer)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => onDelete(influencer)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
